import React, { useState } from 'react';
import { useSDK } from '../context/SDKContext.tsx';
import AiNotice from '../components/AiNotice.tsx';
import {
  User,
  Users,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  AlertTriangle,
  FileText,
  Copy,
  Check,
  RefreshCw,
  AlertCircle,
  Scroll
} from 'lucide-react';

export default function ClauseBenefPage(): React.ReactElement {
  const { sdk } = useSDK();
  const [step, setStep] = useState(1);
  const [objective, setObjective] = useState<'conjoint_uniquement' | 'conjoint_enfants' | 'enfants' | null>(null);
  const [isMinor, setIsMinor] = useState(false);

  // API states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clauseText, setClauseText] = useState('');
  const [templateUsed, setTemplateUsed] = useState('');
  const [score, setScore] = useState<number | null>(null);
  const [niveau, setNiveau] = useState<string>('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Validation state
  const [confirmed, setConfirmed] = useState(false);
  const [validated, setValidated] = useState(false);

  const runGenerationAndAnalysis = () => {
    if (!objective) return;
    setLoading(true);
    setError(null);
    setClauseText('');
    setScore(null);
    setWarnings([]);
    setSuggestion(null);

    sdk.bricks.executeSync('clause_generator', { objectif: objective, is_minor: isMinor })
      .then((genResult) => {
        if (genResult.error) {
          throw new Error(genResult.error.message);
        }
        const generated = genResult.data?.output?.clause_text as string;
        const tempUsed = genResult.data?.output?.template_used as string;
        setClauseText(generated);
        setTemplateUsed(tempUsed);
        return sdk.bricks.executeSync('compliance_validator', { clause_text: generated });
      })
      .then((compResult) => {
        if (!compResult) return;
        if (compResult.error) {
          throw new Error(compResult.error.message);
        }
        const out = compResult.data?.output;
        if (out) {
          setScore(out.score as number);
          setNiveau(out.niveau as string);
          setWarnings(out.warnings as string[]);
          setSuggestion(out.suggestion as string | null);
        }
      })
      .catch((err) => {
        setError((err as Error).message || "Une erreur est survenue lors de l'exécution.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNextToStep2 = () => {
    setStep(2);
    runGenerationAndAnalysis();
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(clauseText);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const applySuggestion = () => {
    if (suggestion) {
      setClauseText(suggestion);
      setLoading(true);
      setError(null);
      setScore(null);
      setWarnings([]);
      setSuggestion(null);

      sdk.bricks.executeSync('compliance_validator', { clause_text: suggestion })
        .then((compResult) => {
          if (compResult.error) {
            throw new Error(compResult.error.message);
          }
          const out = compResult.data?.output;
          if (out) {
            setScore(out.score as number);
            setNiveau(out.niveau as string);
            setWarnings(out.warnings as string[]);
            setSuggestion(out.suggestion as string | null);
          }
        })
        .catch((err) => {
          setError((err as Error).message || "Une erreur est survenue lors du diagnostic.");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
          <Scroll size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Assistant Clause Bénéficiaire</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Générer et analyser la conformité juridique d'une clause bénéficiaire de façon interactive.
          </p>
        </div>
      </div>

      <AiNotice variant="banner" />

      {/* Stepper progress indicator */}
      <div className="flex items-center gap-2 pb-4 border-b border-border">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step === 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            1
          </span>
          <span className={`text-sm font-medium ${step === 1 ? 'text-foreground font-bold' : 'text-muted-foreground'}`}>Configuration</span>
        </div>
        <div className="w-12 h-px bg-border"></div>
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step === 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            2
          </span>
          <span className={`text-sm font-medium ${step === 2 ? 'text-foreground font-bold' : 'text-muted-foreground'}`}>Diagnostic</span>
        </div>
        <div className="w-12 h-px bg-border"></div>
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step === 3 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            3
          </span>
          <span className={`text-sm font-medium ${step === 3 ? 'text-foreground font-bold' : 'text-muted-foreground'}`}>Validation</span>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* STEP 1 */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-foreground">Choisissez l'objectif de répartition</h2>
            <p className="text-sm text-muted-foreground">
              Sélectionnez le type de clause à générer en fonction de la situation de l'assuré.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <button
                type="button"
                onClick={() => setObjective('conjoint_uniquement')}
                className={`flex flex-col items-start text-left p-5 rounded-xl border-2 transition-all ${
                  objective === 'conjoint_uniquement'
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border bg-card hover:border-border/80 hover:bg-muted/30'
                }`}
              >
                <div className={`p-2 rounded-lg ${objective === 'conjoint_uniquement' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'} mb-4`}>
                  <User size={20} />
                </div>
                <h3 className="font-bold text-sm text-foreground">Conjoint survivant</h3>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Le capital est attribué en totalité au conjoint non séparé au jour du décès.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setObjective('conjoint_enfants')}
                className={`flex flex-col items-start text-left p-5 rounded-xl border-2 transition-all ${
                  objective === 'conjoint_enfants'
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border bg-card hover:border-border/80 hover:bg-muted/30'
                }`}
              >
                <div className={`p-2 rounded-lg ${objective === 'conjoint_enfants' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'} mb-4`}>
                  <Users size={20} />
                </div>
                <h3 className="font-bold text-sm text-foreground">Conjoint, puis enfants</h3>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Le conjoint survivant reçoit le capital. En cas de prédécès, il revient aux enfants par parts égales.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setObjective('enfants')}
                className={`flex flex-col items-start text-left p-5 rounded-xl border-2 transition-all ${
                  objective === 'enfants'
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border bg-card hover:border-border/80 hover:bg-muted/30'
                }`}
              >
                <div className={`p-2 rounded-lg ${objective === 'enfants' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'} mb-4`}>
                  <Sparkles size={20} />
                </div>
                <h3 className="font-bold text-sm text-foreground">Enfants uniquement</h3>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Le capital est partagé par parts égales entre tous les enfants nés ou à naître.
                </p>
              </button>
            </div>

            {/* Checkbox minor */}
            <div className="pt-4 flex items-center gap-3">
              <input
                type="checkbox"
                id="isMinor"
                checked={isMinor}
                onChange={(e) => setIsMinor(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="isMinor" className="text-sm font-medium text-foreground select-none cursor-pointer">
                L'assuré ou les bénéficiaires comportent des personnes mineures.
              </label>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              disabled={!objective}
              onClick={handleNextToStep2}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Générer la clause
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="space-y-6">
          {loading ? (
            <div className="bg-card border border-border rounded-xl p-8 shadow-sm flex flex-col items-center justify-center space-y-4">
              <RefreshCw className="animate-spin text-primary" size={32} />
              <p className="text-sm text-muted-foreground font-medium">Génération et diagnostic de conformité en cours…</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Output Preview */}
              <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <FileText size={16} className="text-primary" />
                    Texte de la clause générée
                  </h2>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground font-medium transition-colors"
                  >
                    {copySuccess ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                    {copySuccess ? 'Copié !' : 'Copier'}
                  </button>
                </div>
                <div className="bg-muted/40 rounded-lg p-4 font-mono text-sm leading-relaxed border border-border/50 text-foreground">
                  {clauseText}
                </div>
              </div>

              {/* Conformity Status Card */}
              {score !== null && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Gauge Card */}
                  <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex flex-col items-center justify-center text-center space-y-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Score de conformité</span>
                    <div className="relative flex items-center justify-center w-24 h-24">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" className="text-muted/30" fill="transparent" />
                        <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" className={niveau === 'conforme' ? 'text-green-500' : niveau === 'vigilance' ? 'text-amber-500' : 'text-destructive'} fill="transparent" strokeDasharray="251.2" strokeDashoffset={251.2 - (251.2 * score) / 100} />
                      </svg>
                      <span className="absolute text-xl font-bold text-foreground">{score}%</span>
                    </div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                      niveau === 'conforme' ? 'bg-green-100/80 text-green-800' : niveau === 'vigilance' ? 'bg-amber-100/80 text-amber-800' : 'bg-red-100/80 text-red-800'
                    }`}>
                      {niveau}
                    </span>
                  </div>

                  {/* Warnings / Motifs Column */}
                  <div className="md:col-span-2 bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
                    <h3 className="text-sm font-bold text-foreground">Points d'analyse réglementaire</h3>
                    {warnings.length === 0 ? (
                      <div className="flex items-center gap-2.5 text-green-700 text-sm">
                        <CheckCircle size={18} />
                        <span>Félicitations, aucun problème ou warning identifié sur cette formulation.</span>
                      </div>
                    ) : (
                      <ul className="space-y-3">
                        {warnings.map((w, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-foreground leading-relaxed">
                            <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                            <span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {/* LLM Recommendation Block */}
              {suggestion && (
                <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border border-primary/20 rounded-xl p-6 shadow-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-primary">Reformulation conseillée (IA)</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Le moteur de remédiation propose une alternative optimisée en droit français.</p>
                    </div>
                    <button
                      type="button"
                      onClick={applySuggestion}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      Appliquer la suggestion
                    </button>
                  </div>
                  <div className="bg-background/80 border border-primary/10 rounded-lg p-4 font-mono text-xs text-foreground leading-relaxed">
                    {suggestion}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted transition-colors"
            >
              <ChevronLeft size={16} />
              Retour
            </button>
            <button
              type="button"
              disabled={loading || !clauseText}
              onClick={() => setStep(3)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              Valider la clause
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div className="space-y-6">
          {validated ? (
            <div className="bg-card border border-border rounded-xl p-8 shadow-sm flex flex-col items-center justify-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-green-500">
                <CheckCircle size={36} />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-foreground">Clause validée avec succès !</h2>
                <p className="text-sm text-muted-foreground max-w-md">
                  La clause bénéficiaire a été révisée, diagnostiquée et approuvée par le conseiller. Elle est prête pour impression ou intégration contractuelle.
                </p>
              </div>
              <div className="bg-muted/40 rounded-lg p-4 text-left max-w-lg w-full font-mono text-xs leading-relaxed border border-border/50 text-foreground">
                {clauseText}
              </div>
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setObjective(null);
                  setIsMinor(false);
                  setClauseText('');
                  setScore(null);
                  setValidated(false);
                  setConfirmed(false);
                }}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Créer une nouvelle clause
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
                <h2 className="text-lg font-bold text-foreground">Récapitulatif final de la clause</h2>
                <p className="text-sm text-muted-foreground">Veuillez vérifier les informations ci-dessous avant confirmation.</p>

                <div className="space-y-4 border-t border-border pt-4 text-sm">
                  <div className="grid grid-cols-3 gap-2">
                    <span className="font-semibold text-muted-foreground">Type de répartition :</span>
                    <span className="col-span-2 text-foreground font-medium">
                      {objective === 'conjoint_uniquement' && 'Conjoint uniquement'}
                      {objective === 'conjoint_enfants' && 'Conjoint, puis enfants'}
                      {objective === 'enfants' && 'Enfants uniquement'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="font-semibold text-muted-foreground">Minorité :</span>
                    <span className="col-span-2 text-foreground font-medium">
                      {isMinor ? 'Oui (présence de mineurs)' : 'Non'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="font-semibold text-muted-foreground">Score diagnostiqué :</span>
                    <span className="col-span-2 text-foreground font-medium flex items-center gap-2">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${niveau === 'conforme' ? 'bg-green-500' : niveau === 'vigilance' ? 'bg-amber-500' : 'bg-red-500'}`}></span>
                      {score}% ({niveau})
                    </span>
                  </div>
                  <div className="space-y-2 pt-2">
                    <span className="font-semibold text-muted-foreground">Texte final contractuel :</span>
                    <div className="bg-muted/40 rounded-lg p-4 font-mono text-xs leading-relaxed border border-border/50 text-foreground">
                      {clauseText}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="confirmVerify"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="confirmVerify" className="text-sm font-medium text-foreground select-none cursor-pointer">
                    Je confirme avoir vérifié l'adéquation de cette clause avec la situation patrimoniale de l'assuré.
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted transition-colors"
                >
                  <ChevronLeft size={16} />
                  Retour
                </button>
                <button
                  type="button"
                  disabled={!confirmed}
                  onClick={() => setValidated(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  Valider et terminer
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
